import React from "react";
import { SmsCountMap } from "../containers/UsersManagerContainer";
import Select2 from "../../../components/Select2";
import BluredInput from "../../../components/BluredInput";
import styles from "./UsersManager.module.css";

interface CommonUser {
  id: string;
  username: string;
  fullname: string;
  messagename: string;
  active: boolean;
  organization_id: number;
  email: string | null;
}

interface Doctor extends CommonUser {
  tag: "doctor";
  doc_id: string;
  secretaries: string[];
  compta_user_id: string | null;
  google_business_profile_id: string | null;
}

interface Secretary extends CommonUser {
  tag: "secretary";
}

interface UsersManagerProps {
  users: (Doctor | Secretary)[];
  comptaUsers: {
    _id: string;
    username: string;
    primaryEmail: string | null;
    allEmails: string[];
  }[];
  smsCountMap: SmsCountMap;
  onUpdateFullName: (userId: string, fullName: string) => void;
  onUpdateMessageName: (userId: string, messagename: string) => void;
  onUpdateActiveStatus: (userId: string, active: boolean) => void;
  onUpdateDocId: (userId: string, docId: string) => void;
  onAssignSecretaryToDoctor: (doctorId: string, secretaryId: string) => void;
  onLinkComptaUserToDoctor: (doctorId: string, comptaUserId: string) => void;
  onUnlinkComptaUserFromDoctor: (doctorId: string) => void;
  onAssignRole: (
    userId: string,
    role: "docteur" | "secrétaire" | undefined
  ) => void;
  onAssignOrg: (userId: string, orgId: number) => void;
  onUpdateGoogleId: (userId: string, googleId: string) => void;
  organizations: { id: number; name: string }[];
  isComptaLinkMutating: boolean;
}

const normalizeEmail = (value: string | null | undefined) =>
  (value || "").trim().toLowerCase();

const getComptaUserLabel = (user: {
  username: string;
  primaryEmail: string | null;
}) => {
  if (user.primaryEmail) {
    return `${user.username} (${user.primaryEmail})`;
  }

  return user.username;
};

const UsersManager = (props: UsersManagerProps) => {
  const [selectedComptaUserByDoctorId, setSelectedComptaUserByDoctorId] =
    React.useState<Record<string, string>>({});
  const secretariesKeyMap = props.users
    .filter((u) => u.tag === "secretary")
    .map((u) => u.id)
    .reduce(
      (result, id) => ({
        ...result,
        [id]: props.users.find((u) => u.id === id)!.fullname || "",
      }),
      {} as { [key: string]: string }
    );
  return (
    <table>
      <thead>
        <tr>
          <th>Login</th>
          <th>Actif</th>
          <th>Nom Complet</th>
          <th>Nom sur les messages</th>
          <th>Email</th>
          <th>Compte Compta</th>
          <th>DocId</th>
          <th>GoogleId</th>
          <th>Org</th>
          <th>Rôle</th>
        </tr>
      </thead>
      <tbody>
        {props.users.map((user) => (
          <tr key={user.id + "userKey"}>
            <td>{user.username}</td>
            <td>
              <input
                type="checkbox"
                checked={!!user.active}
                onChange={() =>
                  props.onUpdateActiveStatus(user.id, !user.active)
                }
              />
            </td>
            <td>
              <BluredInput
                initialValue={user.fullname || ""}
                onBlur={(value) => props.onUpdateFullName(user.id, value)}
                className={""}
              />
            </td>
            <td>
              <BluredInput
                initialValue={user.messagename || ""}
                onBlur={(value) => props.onUpdateMessageName(user.id, value)}
                className={styles.tdAuto}
              />
            </td>
            <td>{user.email || "-"}</td>
            <td>
              {user.tag === "doctor" ? (
                (() => {
                  const suggestedComptaUser = props.comptaUsers.find((comptaUser) => {
                    const doctorEmail = normalizeEmail(user.email);
                    if (!doctorEmail) {
                      return false;
                    }

                    return comptaUser.allEmails.some(
                      (email) => normalizeEmail(email) === doctorEmail,
                    );
                  });
                  const linkedComptaUser = props.comptaUsers.find(
                    (comptaUser) => comptaUser._id === user.compta_user_id,
                  );
                  const selectedComptaUserId =
                    selectedComptaUserByDoctorId[user.id] ||
                    user.compta_user_id ||
                    suggestedComptaUser?._id ||
                    "";

                  return (
                    <div>
                      <div>{linkedComptaUser ? getComptaUserLabel(linkedComptaUser) : "Non lié"}</div>
                      {!linkedComptaUser && suggestedComptaUser ? (
                        <div className={styles.tdAuto}>
                          Suggestion email: {getComptaUserLabel(suggestedComptaUser)}
                        </div>
                      ) : null}
                      <select
                        value={selectedComptaUserId}
                        onChange={(event) =>
                          setSelectedComptaUserByDoctorId((prev) => ({
                            ...prev,
                            [user.id]: event.target.value,
                          }))
                        }
                        disabled={props.isComptaLinkMutating}
                      >
                        <option value="">Choisir un compte Compta</option>
                        {props.comptaUsers.map((comptaUser) => (
                          <option key={comptaUser._id} value={comptaUser._id}>
                            {getComptaUserLabel(comptaUser)}
                          </option>
                        ))}
                      </select>
                      <div>
                        <button
                          type="button"
                          disabled={!selectedComptaUserId || props.isComptaLinkMutating}
                          onClick={() =>
                            props.onLinkComptaUserToDoctor(user.id, selectedComptaUserId)
                          }
                        >
                          Lier
                        </button>
                        <button
                          type="button"
                          disabled={!user.compta_user_id || props.isComptaLinkMutating}
                          onClick={() => props.onUnlinkComptaUserFromDoctor(user.id)}
                        >
                          Délier
                        </button>
                      </div>
                    </div>
                  );
                })()
              ) : (
                "-"
              )}
            </td>
            <td key={"docIdInput" + user.id}>
              <input
                type="text"
                value={user.tag === "doctor" ? user.doc_id : ""}
                onChange={(e) => props.onUpdateDocId(user.id, e.target.value)}
                disabled={user.tag !== "doctor"}
                placeholder="docId"
                className={styles.td1}
              />
            </td>
            <td key={"googleIdInput" + user.id}>
              <input
                type="text"
                value={
                  user.tag === "doctor"
                    ? user.google_business_profile_id || ""
                    : ""
                }
                onChange={(e) =>
                  props.onUpdateGoogleId(user.id, e.target.value)
                }
                placeholder="Google ID"
                className={styles.td1}
                disabled={user.tag !== "doctor"}
              />
            </td>
            <td key={"orgInput" + user.id}>
              <select
                value={user.organization_id || 0}
                onChange={(e) =>
                  props.onAssignOrg(user.id, parseInt(e.target.value))
                }
              >
                {props.organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </td>
            <td>
              <Select2
                keyMap={{ docteur: "Docteur", secrétaire: "Secrétaire" }}
                defaultString="Pas de rôle"
                onSelect={(role: "docteur" | "secrétaire" | undefined) =>
                  props.onAssignRole(user.id, role)
                }
                selected={
                  user.tag === "doctor"
                    ? "docteur"
                    : user.tag === "secretary"
                      ? "secrétaire"
                      : undefined
                }
                className={""}
              />
            </td>
            {user.tag === "secretary" ? null : (
              <>
                {user.tag === "doctor" ? (
                  user.secretaries.map((id) => (
                    <td key={id + "secrétaireSelectKey"}>
                      <Select2
                        keyMap={secretariesKeyMap}
                        defaultString="vide"
                        onSelect={(_id: string | undefined) => {
                          if (!_id) return;
                          props.onAssignSecretaryToDoctor(user.id, _id);
                        }}
                        selected={id}
                        className={""}
                      />
                    </td>
                  ))
                ) : (
                  <></>
                )}
                <td
                  key={
                    (user.tag === "doctor" &&
                      user.secretaries &&
                      user.secretaries.length + "secrétaireSelectKey") ||
                    0 + "secrétaireSelectKey"
                  }
                >
                  <Select2
                    keyMap={secretariesKeyMap}
                    defaultString="vide"
                    onSelect={(_id: string | undefined) => {
                      _id && props.onAssignSecretaryToDoctor(user.id, _id);
                    }}
                    selected={undefined}
                    className={""}
                  />
                </td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default UsersManager;
